import React from "react";
import { useForm } from "react-hook-form";
import { useApi } from "@tiny/api";
import { usePostSegmentImportMutation } from "@tiny/api";
import { useMessages } from "@tiny/components";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { TinyImportDialogProps } from "./TinyImporter.types";

interface FormValues {
  name: string;
}

export function useTinyImportDialog(props: TinyImportDialogProps) {
  const api = useApi();
  const { onClose, onSuccess } = props;

  const { showErrorMessage } = useMessages();

  const [allowAddAudiences, setAllowAddAudiences] = React.useState(true);

  const [executing, setExecuting] = React.useState(false);

  const form = useForm<FormValues>();

  const { mutateAsync: postSegmentMutateAsync } =
    usePostSegmentImportMutation();

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!allowAddAudiences) {
        return;
      }
      setExecuting(true);
      try {
        const { data: exist } = await api.putSegmentCheckExists({
          name: values.name,
          type: "UPLOAD",
        });
        if (exist) {
          form.setError("name", {
            type: "validate",
            message: "Audience with this name already exists!",
          });
        } else {
          onClose();
          const { name } = values;
          if (name) {
            onSuccess(name);
          }
        }
      } catch (e: any) {
        extractBackendError(e.response).then((be) =>
          showErrorMessage(getErrorMessage(be))
        );
      } finally {
        setExecuting(false);
      }
    },
    [
      allowAddAudiences,
      form,
      onClose,
      postSegmentMutateAsync,
      onSuccess,
      showErrorMessage,
    ]
  );

  const continueAdding = React.useCallback(() => {
    form.handleSubmit(handleSubmit)();
  }, [form, handleSubmit]);

  return {
    allowAddAudiences,
    setAllowAddAudiences,
    form,
    handleSubmit,
    continueAdding,
    executing,
  };
}
